import { useState } from 'react'
import { fetchTillConfig } from '@/components/KioskShell'
import { gql } from '@/lib/api'
import { CREATE_ACCESS_TOKEN } from '@/lib/queries'
import { useKioskStore } from '@/stores/kiosk-store'

// Login-skärm som visas ALLRA FÖRST när kiosken öppnas på en ny enhet.
// Token sparas permanent i localStorage → nästa gång enheten startas
// hoppas login-skärmen över.

const TOKEN_KEY = 'kiosk_token'

export default function LoginScreen() {
  const config = useKioskStore((s) => s.config)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const setParseToken = useKioskStore((s) => s.setParseToken)
  const setStep = useKioskStore((s) => s.setStep)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async () => {
    if (!config || !username || !password) return
    setLoading(true)
    setError(null)
    try {
      const data = await gql(config.apiEndpoint, CREATE_ACCESS_TOKEN, { username, password })
      const token = data.createAccessToken.parseToken
      localStorage.setItem(TOKEN_KEY, token)
      setParseToken(token)
      await fetchTillConfig(config.apiEndpoint, token, config.restaurantId, config.tillId)
      setStep('menuTypeSelect')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Inloggningen misslyckades')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center bg-kiosk-bg px-8">
      {/* Logo + branding */}
      <div className="mb-8 flex flex-col items-center">
        {remoteConfig?.logoUrl && (
          <div className="w-28 h-28 rounded-full overflow-hidden border-3 border-white/20 shadow-2xl bg-black/40 mb-4">
            <img src={remoteConfig.logoUrl} alt={remoteConfig?.appName ?? ''} className="w-full h-full object-cover" />
          </div>
        )}
        <h1 className="text-3xl font-bold text-kiosk-accent tracking-widest drop-shadow-lg">{(remoteConfig?.appName || 'Loco Kiosk').toUpperCase()}</h1>
      </div>

      <div className="w-full max-w-sm bg-kiosk-surface rounded-3xl p-8 shadow-2xl">
        <h2 className="text-2xl font-bold text-kiosk-text text-center mb-2">Logga in</h2>
        <p className="text-sm text-kiosk-muted text-center mb-6">Första gången på den här enheten — logga in för att aktivera kiosken. Du kommer inte behöva göra det igen.</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-kiosk-muted block mb-1">Användarnamn</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="namn@exempel.se"
              autoComplete="username"
              className="w-full bg-kiosk-bg border border-kiosk-surface-2 rounded-xl px-4 py-3 text-kiosk-text text-base focus:outline-none focus:border-kiosk-accent"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleLogin()
              }}
            />
          </div>

          <div>
            <label className="text-xs text-kiosk-muted block mb-1">Lösenord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-kiosk-bg border border-kiosk-surface-2 rounded-xl px-4 py-3 text-kiosk-text text-base focus:outline-none focus:border-kiosk-accent"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleLogin()
              }}
            />
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-2 rounded-xl">{error}</div>}

          <button onClick={handleLogin} disabled={loading || !username || !password} className="kiosk-btn w-full py-4 bg-kiosk-accent text-kiosk-bg font-bold rounded-xl text-lg disabled:opacity-50">
            {loading ? 'Loggar in…' : 'Logga in'}
          </button>

          <p className="text-xs text-kiosk-muted text-center pt-2">Använd dina vanliga LocoPOS-uppgifter</p>
        </div>
      </div>
    </div>
  )
}
